import { useState, useEffect, useRef } from 'react';
import apiClient from '@/lib/api/client';

interface ScrapedProduct {
  name: string;
  sku: string | null;
  unit_price: number | null;
  unit_of_measure: string;
  description: string | null;
  source_url: string;
  brand?: string;
}

interface Category {
  id: number;
  name: string;
  parent_id: number | null;
}

// The subset of Product fields the cart needs
export interface ImportableProduct {
  id: number;
  name: string;
  sku: string;
  description: string | null;
  unit_price: number;
  unit_of_measure: string;
  current_stock: number | null;
  reorder_point: number | null;
  supplier_name: string | null;
  lead_time_days: number | null;
  source_url?: string;
}

interface EditState {
  name: string;
  sku: string;
  price: string;
  uom: string;
  categoryId: string;
  qty: string;
}

interface ImportPanelProps {
  onAddToCart: (product: ImportableProduct, qty: number) => void;
}

const UOM_OPTIONS = ['each', 'linear ft', 'board ft', 'sheet', 'bag', 'pail', 'box', 'roll', 'sq ft', 'pair', 'set'];

export const ImportPanel = ({ onAddToCart }: ImportPanelProps) => {
  const [url, setUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [results, setResults] = useState<ScrapedProduct[]>([]);
  const [edits, setEdits] = useState<EditState[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const [savedIds, setSavedIds] = useState<Map<number, number>>(new Map());
  const [fetchBlocked, setFetchBlocked] = useState(false);
  const [clientFetching, setClientFetching] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pastedHtml, setPastedHtml] = useState('');
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiClient.get('/catalog/categories')
      .then(r => setCategories((r.data || []) as Category[]))
      .catch(() => {});
    urlRef.current?.focus();
  }, []);

  const initEdits = (prods: ScrapedProduct[]): EditState[] =>
    prods.map(p => ({
      name: p.name,
      sku: p.sku ?? '',
      price: p.unit_price != null ? p.unit_price.toFixed(2) : '',
      uom: p.unit_of_measure || 'each',
      categoryId: '',
      qty: '1',
    }));

  const applyProducts = (data: ScrapedProduct[]) => {
    setResults(data);
    setEdits(initEdits(data));
    setFetchError(null);
    setFetchBlocked(false);
    setClientFetching(false);
    setShowPaste(false);
    setPastedHtml('');
  };

  const setEdit = (i: number, partial: Partial<EditState>) => {
    setEdits(prev => prev.map((e, idx) => idx === i ? { ...e, ...partial } : e));
  };

  const addManualEntry = (sourceUrl = '') => {
    const blank: ScrapedProduct = { name: '', sku: null, unit_price: null, unit_of_measure: 'each', description: null, source_url: sourceUrl };
    setResults(prev => [...prev, blank]);
    setEdits(prev => [...prev, { name: '', sku: '', price: '', uom: 'each', categoryId: '', qty: '1' }]);
    setSavedIds(prev => { const m = new Map(prev); return m; });
  };

  // Try fetching via the user's own browser (bypasses Cloudflare IP block).
  // Falls back to showPaste if CORS prevents reading the response body.
  const tryClientFetch = async (targetUrl: string) => {
    setClientFetching(true);
    setFetchError('Server-side fetch blocked. Trying via your browser…');
    try {
      const r = await fetch(targetUrl);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const html = await r.text();
      const res = await apiClient.post('/catalog/parse-html', { html, url: targetUrl });
      const data: ScrapedProduct[] = res.data?.data || [];
      if (data.length === 0) {
        setFetchError(res.data?.message || 'No products found. Try pasting the page source below.');
        setShowPaste(true);
      } else {
        applyProducts(data);
      }
    } catch {
      // CORS prevents reading the response — guide user to paste the source
      setFetchError('Browser fetch blocked by CORS. Paste the page source below to extract products.');
      setShowPaste(true);
    } finally {
      setClientFetching(false);
    }
  };

  const handleFetch = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setFetching(true);
    setFetchError(null);
    setFetchBlocked(false);
    setShowPaste(false);
    setPastedHtml('');
    setResults([]);
    setSavedIds(new Map());
    try {
      const r = await apiClient.post('/catalog/scrape', { url: trimmed });
      const data: ScrapedProduct[] = r.data?.data || [];
      if (data.length === 0) {
        setFetchError(r.data?.message || 'No products found at this URL. Try a product detail page.');
      } else {
        setResults(data);
        setEdits(initEdits(data));
      }
    } catch (e: unknown) {
      const resp = (e as { response?: { data?: { error?: string; blocked?: boolean } } })?.response?.data;
      if (resp?.blocked) {
        setFetchBlocked(true);
        setFetching(false);
        await tryClientFetch(trimmed);
        return;
      }
      setFetchError(resp?.error || 'Failed to fetch. The site may block automated requests.');
    } finally {
      setFetching(false);
    }
  };

  const handleParseHtml = async () => {
    const html = pastedHtml.trim();
    if (!html) return;
    setFetching(true);
    try {
      const res = await apiClient.post('/catalog/parse-html', { html, url: url.trim() });
      const data: ScrapedProduct[] = res.data?.data || [];
      if (data.length === 0) {
        setFetchError(res.data?.message || 'No product data found. Make sure you copied the full page source.');
      } else {
        applyProducts(data);
      }
    } catch {
      setFetchError('Failed to parse the pasted HTML.');
    } finally {
      setFetching(false);
    }
  };

  const saveOne = async (i: number): Promise<number> => {
    if (savedIds.has(i)) return savedIds.get(i)!;
    const e = edits[i];
    const raw = results[i];
    const r = await apiClient.post('/catalog/products', {
      name: e.name || raw.name,
      sku: e.sku || null,
      unit_price: parseFloat(e.price) || 0,
      unit_of_measure: e.uom || 'each',
      description: raw.description,
      source_url: raw.source_url,
      category_id: e.categoryId ? parseInt(e.categoryId) : null,
    });
    const newId: number = r.data.id;
    setSavedIds(prev => { const m = new Map(prev); m.set(i, newId); return m; });
    return newId;
  };

  const handleSaveToCatalog = async (i: number) => {
    setActionError(null);
    setSaving(prev => { const s = new Set(prev); s.add(i); return s; });
    try {
      await saveOne(i);
    } catch {
      setActionError('Failed to save to catalog.');
    } finally {
      setSaving(prev => { const s = new Set(prev); s.delete(i); return s; });
    }
  };

  const handleAddToCart = async (i: number) => {
    setActionError(null);
    setSaving(prev => { const s = new Set(prev); s.add(i); return s; });
    try {
      const dbId = await saveOne(i);
      const e = edits[i];
      const raw = results[i];
      const product: ImportableProduct = {
        id: dbId,
        name: e.name || raw.name,
        sku: e.sku || '',
        description: raw.description,
        unit_price: parseFloat(e.price) || 0,
        unit_of_measure: e.uom || 'each',
        current_stock: null,
        reorder_point: null,
        supplier_name: raw.brand ?? null,
        lead_time_days: null,
        source_url: raw.source_url,
      };
      const qty = parseInt(e.qty) || 1;
      onAddToCart(product, qty);
    } catch {
      setActionError('Failed to add to cart. Could not save product to catalog.');
    } finally {
      setSaving(prev => { const s = new Set(prev); s.delete(i); return s; });
    }
  };

  const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary';
  const rootCategories = categories.filter(c => c.parent_id === null).sort((a, b) => a.name.localeCompare(b.name));
  const childrenOf = (pid: number) => categories.filter(c => c.parent_id === pid).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="border-t border-slate-200 bg-slate-50/80 px-6 py-4">
      {/* URL input row */}
      <div className="flex items-center gap-2 mb-3">
        <span className="material-symbols-outlined text-slate-400 text-base flex-shrink-0">link</span>
        <input
          ref={urlRef}
          type="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleFetch()}
          placeholder="Paste a product page or search results URL (e.g. homedepot.ca, rona.ca)…"
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          onClick={handleFetch}
          disabled={fetching || clientFetching || !url.trim()}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex-shrink-0"
        >
          {fetching || clientFetching
            ? <><span className="material-symbols-outlined text-base animate-spin">autorenew</span>Fetching…</>
            : <><span className="material-symbols-outlined text-base">download</span>Fetch</>}
        </button>
      </div>

      {fetchError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-3">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-red-400 text-base mt-0.5 flex-shrink-0">
              {clientFetching ? 'autorenew' : 'error_outline'}
            </span>
            <div className="flex-1">
              <p>{fetchError}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  onClick={() => { setFetchError(null); setFetchBlocked(false); setShowPaste(false); addManualEntry(url.trim()); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">edit_note</span>
                  Enter manually
                </button>
                {(fetchBlocked || showPaste) && !showPaste && (
                  <button
                    onClick={() => setShowPaste(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">content_paste</span>
                    Paste page source
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Paste HTML section */}
          {showPaste && (
            <div className="mt-3 pt-3 border-t border-red-200">
              <p className="text-xs text-red-600 font-semibold mb-1">Paste page source</p>
              <ol className="text-xs text-red-500 mb-2 space-y-0.5 list-decimal list-inside">
                <li>Open the URL in your browser tab</li>
                <li>Press <kbd className="px-1 py-0.5 rounded bg-red-100 font-mono">Ctrl+U</kbd> (or right-click → View Page Source)</li>
                <li>Press <kbd className="px-1 py-0.5 rounded bg-red-100 font-mono">Ctrl+A</kbd> then <kbd className="px-1 py-0.5 rounded bg-red-100 font-mono">Ctrl+C</kbd></li>
                <li>Paste below and click Parse</li>
              </ol>
              <textarea
                autoFocus
                value={pastedHtml}
                onChange={e => setPastedHtml(e.target.value)}
                placeholder="Paste full HTML source here…"
                className="w-full h-20 px-2 py-1.5 text-xs font-mono border border-red-300 rounded-lg bg-white resize-y focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={handleParseHtml}
                disabled={!pastedHtml.trim() || fetching}
                className="mt-1.5 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                <span className="material-symbols-outlined text-sm">parse</span>
                Parse
              </button>
            </div>
          )}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-3 flex items-center gap-2">
          <span className="material-symbols-outlined text-base flex-shrink-0">error_outline</span>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}

      {results.length === 0 && !fetching && !clientFetching && !fetchError && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            Works with most product pages (JSON-LD). Search results supported for Next.js sites (Home Depot, etc.). Rona uses Cloudflare — paste page source if auto-fetch fails.
          </p>
          <button
            onClick={() => addManualEntry()}
            className="ml-4 flex-shrink-0 flex items-center gap-1 text-xs text-primary font-semibold hover:underline"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Enter manually
          </button>
        </div>
      )}

      {results.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {results.length} product{results.length > 1 ? 's' : ''} found
            </p>
            {results.length > 1 && (
              <button
                onClick={async () => {
                  for (let i = 0; i < results.length; i++) {
                    if (!savedIds.has(i)) {
                      await handleAddToCart(i).catch(() => {});
                    }
                  }
                }}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Add All to Cart
              </button>
            )}
          </div>
          <div className={`grid gap-3 ${results.length === 1 ? 'grid-cols-1 max-w-xl' : 'grid-cols-1 md:grid-cols-2'}`}>
            {results.map((product, i) => {
              const e = edits[i] ?? { name: product.name, sku: product.sku ?? '', price: '', uom: 'each', categoryId: '', qty: '1' };
              const isSaving = saving.has(i);
              const isSaved = savedIds.has(i);
              return (
                <div key={i} className={`rounded-xl border bg-white p-4 flex flex-col gap-3 shadow-sm ${isSaved ? 'border-green-300 bg-green-50/40' : 'border-slate-200'}`}>
                  {/* Name */}
                  <input
                    className={inputCls}
                    value={e.name}
                    onChange={ev => setEdit(i, { name: ev.target.value })}
                    placeholder="Product name"
                  />
                  {/* SKU + Price + UOM row */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">SKU</label>
                      <input
                        className={inputCls}
                        value={e.sku}
                        onChange={ev => setEdit(i, { sku: ev.target.value })}
                        placeholder="SKU"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">Unit Price ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputCls}
                        value={e.price}
                        onChange={ev => setEdit(i, { price: ev.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">UOM</label>
                      <input
                        list={`uom-list-${i}`}
                        className={inputCls}
                        value={e.uom}
                        onChange={ev => setEdit(i, { uom: ev.target.value })}
                        placeholder="each"
                      />
                      <datalist id={`uom-list-${i}`}>
                        {UOM_OPTIONS.map(u => <option key={u} value={u} />)}
                      </datalist>
                    </div>
                  </div>
                  {/* Category + Qty */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">Category</label>
                      <select
                        className={inputCls}
                        value={e.categoryId}
                        onChange={ev => setEdit(i, { categoryId: ev.target.value })}
                      >
                        <option value="">— Uncategorized —</option>
                        {rootCategories.map(cat => (
                          <optgroup key={cat.id} label={cat.name}>
                            <option value={cat.id}>{cat.name}</option>
                            {childrenOf(cat.id).map(child => (
                              <option key={child.id} value={child.id}>
                                &nbsp;&nbsp;{child.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">Qty to Cart</label>
                      <input
                        type="number"
                        min="1"
                        className={inputCls}
                        value={e.qty}
                        onChange={ev => setEdit(i, { qty: ev.target.value })}
                      />
                    </div>
                  </div>
                  {/* Source */}
                  <p className="text-[10px] text-slate-400 truncate">
                    <span className="font-semibold">Source:</span>{' '}
                    {product.brand && <span className="text-slate-500">{product.brand} · </span>}
                    <a href={product.source_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {product.source_url}
                    </a>
                  </p>
                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                    {isSaved ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-semibold">
                        <span className="material-symbols-outlined text-sm">check_circle</span>
                        Saved to Catalog
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSaveToCatalog(i)}
                        disabled={isSaving}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100 disabled:opacity-50 transition-colors"
                      >
                        <span className="material-symbols-outlined text-sm">save</span>
                        {isSaving ? 'Saving…' : 'Save to Catalog'}
                      </button>
                    )}
                    <button
                      onClick={() => handleAddToCart(i)}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity ml-auto"
                    >
                      <span className="material-symbols-outlined text-sm">shopping_cart</span>
                      {isSaving ? 'Adding…' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
